//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLocation } from 'react-router-dom';
import Switcher from './switcher';
import { useAuthHelper } from '../../AuthHelper';
import { useConfig } from '../../Config';
import { useTranslation } from 'react-i18next';
import { isSafeRedirectUrl } from '../../utils/validateUrlUtils';
import { safeNavigateTop, sanitizeIframeSrc } from '../../utils/securityUtils';
import { useLoopContext } from '../../LoopContext';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useActiveLoopContext } from '../../ActiveLoopContext';
import { useContextBridge } from '../../hooks/useContextBridge';
import storageService from '../../utils/storageService';
import { RETURN_URL_KEY } from '../../oidcConnect';

interface SwitcherIframeProps {
  iframeSrc: string;
  appName: string;
  path: string;
  switcherPosition?: 'left' | 'right';
  syncBrowserUrl?: boolean;
  openExternalLinksInNewTab?: boolean;
}

// Poll the iframe URL frequently so we can detect cross-app navigations and
// re-show the loading overlay quickly. Kept short (150 ms) to minimize the
// white-screen flash window between a click in the child app and the next
// document beginning to paint.
export const URL_CHANGE_INTERVAL = 150;

const MODERNIZED_APPS = [
  'Test',
  'Build',
  'Code',
  'Control',
  'Measure',
  'Release',
  'Plan',
  'Deploy',
];

export function getSwitcherPositionForApp(appName: string): 'left' | 'right' {
  return MODERNIZED_APPS.some(
    (name) => name.toLowerCase() === appName.toLowerCase(),
  )
    ? 'right'
    : 'left';
}

function extractAppNameFromPath(path: string): string {
  // Handles both /solution/appname and /appname/rest
  const match = path.match(/^\/solution\/([^\/?#]+)|^\/([^\/?#]+)/);
  const matchFound = match ? match[1] || match[2] || '' : '';
  return matchFound.includes('velocity') ? 'measure' : matchFound; // If Velocity app
}

function capitalizeFirstLetter(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ─────────────────────────────────────────────────────────────────────────────
// Iframe URL persistence (NEXUS00005880)
// ─────────────────────────────────────────────────────────────────────────────
// Persist the last-known iframe URL per app in sessionStorage so a browser hard
// refresh restores the user's current location inside the point product instead
// of bouncing them back to that product's home page.
//
// Storage choice: sessionStorage (per-tab). Survives F5 refresh, cleared on tab
// close — matches a "session"-scoped expectation and avoids leaking state across
// users on shared browsers.

function getIframeUrlStorageKey(appName: string): string {
  return `${storageService.KEYS.IFRAME_URL_PREFIX}${(appName || '').toLowerCase()}`;
}

function getNavigationType(): string | null {
  try {
    const entries = globalThis.performance?.getEntriesByType?.('navigation');
    const navEntry = entries?.[0] as
      | (PerformanceEntry & { type?: string })
      | undefined;
    if (typeof navEntry?.type === 'string') {
      return navEntry.type;
    }
  } catch {
    // Ignore and try deprecated fallback below.
  }

  try {
    const legacyType = (
      globalThis.performance as Performance & {
        navigation?: { type?: number };
      }
    )?.navigation?.type;
    if (legacyType === 1) return 'reload';
    if (legacyType === 2) return 'back_forward';
    if (legacyType === 0) return 'navigate';
  } catch {
    // Ignore and use safe default.
  }

  return null;
}

function shouldRestoreStoredUrlOnMount(): boolean {
  const navType = getNavigationType();
  // For explicit address-bar navigations, honor the canonical route-derived src.
  if (navType === 'navigate') return false;
  // For reload/back-forward/unknown environments, keep restore behavior.
  return true;
}

// Same-origin, non-blank, non-auth/-logout URLs are safe to persist and restore.
// Anything else (cross-origin Keycloak page, about:blank, parent logout/login)
// would dump the user back into an auth flow on next refresh — never persist it.
function isPersistableIframeUrl(
  url: string,
  issuerPath: string,
  baseName: string,
): boolean {
  if (!url || url === 'about:blank') return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.origin !== globalThis.location.origin) return false;
  if (parsed.pathname.startsWith(issuerPath)) return false;
  const p = parsed.pathname;
  if (
    p === `${baseName}/logout` ||
    p === `${baseName}/logged-out` ||
    p === `${baseName}/login` ||
    p.endsWith('/logout') ||
    p.endsWith('/logged-out')
  ) {
    return false;
  }
  return true;
}

// Read the stored URL and only return it if it's still safe to restore AND it
// belongs to the requested app (defensive against key collisions or stale
// entries that point at a different product).
export function getStoredIframeUrl(
  appName: string,
  fallbackSrc: string,
  issuerPath: string,
  baseName: string,
): string {
  if (!appName) return fallbackSrc;
  const stored = storageService.getSessionItem(getIframeUrlStorageKey(appName));
  if (!stored) return fallbackSrc;
  if (!isPersistableIframeUrl(stored, issuerPath, baseName)) return fallbackSrc;
  // Make sure the stored URL still targets the same app as the fallback. The
  // fallback `iframeSrc` is the canonical product entry path (e.g. "/plan/").
  try {
    const storedPath = new URL(stored).pathname;
    const fallbackPath = new URL(fallbackSrc, globalThis.location.origin)
      .pathname;
    const fallbackSegment = fallbackPath.split('/').find(Boolean);
    if (fallbackSegment) {
      const prefix = `/${fallbackSegment}`;
      // Exact match OR a child path of the segment — but not a longer-named
      // sibling app (e.g. "/plan" must not match a future "/plan-v2/...").
      if (storedPath !== prefix && !storedPath.startsWith(`${prefix}/`)) {
        return fallbackSrc;
      }
    }
  } catch {
    return fallbackSrc;
  }
  return stored;
}

const SwitcherIframe: React.FC<SwitcherIframeProps> = ({
  iframeSrc,
  appName,
  path,
  switcherPosition,
  syncBrowserUrl = true,
  openExternalLinksInNewTab = false,
}) => {
  const resolvedSwitcherPosition =
    switcherPosition ?? getSwitcherPositionForApp(appName);
  const location = useLocation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const redirectRef = useRef<boolean>(false);
  const iframeHadContent = useRef<boolean>(false);
  // Tracks whether the kc-logout click listener has been attached so the
  // polling interval doesn't add duplicate listeners across multiple polls.
  const logoutButtonWiredRef = useRef<boolean>(false);
  const prevIframeSrcRef = useRef<string | null>(null);
  const prevLocationKeyRef = useRef<string | null>(null);
  const [, setUrlHistory] = useState<string[]>([]);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const { resetInactivityTimer } = useAuthHelper();
  const [isSwitcherEnabled, setIsSwitcherEnabled] = useState(true);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const { onLogout } = useAuthHelper();
  const { baseName, oidc } = useConfig();
  const { issuer } = oidc;
  const issuerPath = new URL(issuer).pathname;
  const { t } = useTranslation();
  const { loopLinksResponseInContext } = useLoopContext();
  const loopLinks = loopLinksResponseInContext ?? {}; // Adjust the following lines based on the actual structure of loopLinks
  const teamspaceName = loopLinks?.teamspaceName || '';
  const loopName = loopLinks?.loopName || '';
  const {
    activeLoopId: contextActiveLoopId,
    activeLoopName: contextActiveLoopName,
    activeTeamspaceId: contextActiveTeamspaceId,
  } = useActiveLoopContext();
  // ctxTeamspaceName is the source useContextBridge actually broadcasts in the
  // CONTEXT_UPDATE payload — NOT the `teamspaceName` above (that one is
  // LoopContext-derived and only used locally for button test IDs). Use this
  // one for the broadcast-trigger effect's deps so a teamspace rename in
  // TeamspaceContext isn't missed.
  const { teamspaceId: teamspaceCtxId, teamspaceName: ctxTeamspaceName } =
    useTeamspaceContext();

  // Add context params to iframe URL for all products (Query Params Method)
  // NOTE: Context is passed via URL query parameters, which means it will be:
  // - Visible in browser address bar, network tab, and server logs
  // - Included in Referer headers when navigating between products
  // - Potentially cached by browsers and proxies
  // Only opaque IDs (teamspaceId, loopId) go in the URL for this reason.
  // Display names (teamspaceName, loopName) are delivered via postMessage only
  // (see useContextBridge), which is not logged or cached.
  //
  // Benefits:
  // - Survives browser refresh (query params persist)
  // - Enables cross-app navigation with context
  // - Point products can initialize state from URL params on load
  //
  // NEXUS00005880: prefer the last-persisted iframe URL for this app over the
  // canonical entry path so a browser refresh restores the user's location.
  // Extract allowed origin from the configured iframeSrc for cross-origin solution apps
  const allowedIframeOrigins = React.useMemo(() => {
    try {
      const url = new URL(iframeSrc, window.location.origin);
      return url.origin !== window.location.origin ? [url.origin] : [];
    } catch {
      return [];
    }
  }, [iframeSrc]);

  // Context bridge — broadcasts/receives context messages to/from the iframe (postMessage).
  // This handles real-time context updates when user switches loops/teamspaces.
  const bridge = useContextBridge({
    getIframe: () => iframeRef.current,
    allowedOrigins: allowedIframeOrigins,
  });

  // Captured once per iframe mount/app-switch so the src memo below only
  // recomputes for a genuine navigation, not for every context change —
  // otherwise the iframe would reload on each loop/teamspace switch. Live
  // updates travel via postMessage instead. Re-captured synchronously during
  // render (not in an effect) whenever iframeSrc changes, so the same render
  // that picks up the new app also bakes in the latest context. This mirrors
  // React's documented "adjust a ref during render" pattern: the comparison
  // key (iframeSrc) is checked before writing, so repeated/double-invoked
  // renders (StrictMode) are idempotent — the second invocation sees the ref
  // already updated and no-ops.
  const lastIframeSrcForContextRef = useRef(iframeSrc);
  const initialContextParamsRef = useRef<{
    teamspaceId: string | null | undefined;
    loopId: string | null | undefined;
  }>({
    teamspaceId: contextActiveTeamspaceId || teamspaceCtxId,
    loopId: contextActiveLoopId,
  });
  if (lastIframeSrcForContextRef.current !== iframeSrc) {
    lastIframeSrcForContextRef.current = iframeSrc;
    initialContextParamsRef.current = {
      teamspaceId: contextActiveTeamspaceId || teamspaceCtxId,
      loopId: contextActiveLoopId,
    };
  }

  // Hydration correction: TeamspaceContext/ActiveLoopContext can still be
  // empty on first mount (not yet loaded from the backend). Keep re-syncing
  // the frozen snapshot from live values until a real teamspaceId is seen for
  // the first time, then latch and never touch it again — this intentionally
  // does NOT depend on iframe load state (unreliable for cross-origin
  // products, where reading contentWindow.location throws), so it correctly
  // freezes exactly once real context exists, regardless of origin.
  const [, forceContextParamsRerender] = useState(0);
  const hasCapturedRealTeamspaceIdRef = useRef(
    Boolean(contextActiveTeamspaceId || teamspaceCtxId),
  );
  useEffect(() => {
    if (hasCapturedRealTeamspaceIdRef.current) return;
    const liveTeamspaceId = contextActiveTeamspaceId || teamspaceCtxId;
    if (!liveTeamspaceId) return;
    hasCapturedRealTeamspaceIdRef.current = true;
    initialContextParamsRef.current = {
      teamspaceId: liveTeamspaceId,
      loopId: contextActiveLoopId,
    };
    forceContextParamsRerender((n) => n + 1);
  }, [contextActiveTeamspaceId, teamspaceCtxId, contextActiveLoopId]);

  // Helper: Build iframe src with only identifier params (teamspaceId, loopId).
  // Stable reference (no deps) — see initialContextParamsRef above.
  const addContextToIframeUrl = useCallback((baseSrc: string): string => {
    if (!baseSrc) return baseSrc;
    try {
      const url = new URL(baseSrc, window.location.origin);
      const { teamspaceId, loopId } = initialContextParamsRef.current;

      if (teamspaceId) {
        url.searchParams.set('teamspaceId', teamspaceId);
      } else {
        url.searchParams.delete('teamspaceId');
      }

      if (loopId) {
        url.searchParams.set('loopId', loopId);
      } else {
        url.searchParams.delete('loopId');
      }

      // Never put free-text/sensitive values in the URL — postMessage only.
      url.searchParams.delete('teamspaceName');
      url.searchParams.delete('loopName');
      url.searchParams.delete('teamspaceSlug');

      return `${url.origin}${url.pathname}${url.search}${url.hash}`;
    } catch {
      return baseSrc;
    }
  }, []);

  const iframeSrcWithParams = React.useMemo(() => {
    // Use stored URL only if iframeSrc hasn't changed AND stored URL exists.
    // Note: location-based clearing happens in a separate effect (don't check
    // window.location here — polling interval hasn't run yet on mount).
    const isFirstMount = prevIframeSrcRef.current === null;
    const didIframeSrcChange =
      !isFirstMount && prevIframeSrcRef.current !== iframeSrc;
    const shouldUseStoredUrl =
      !didIframeSrcChange && (!isFirstMount || shouldRestoreStoredUrlOnMount());
    const baseSrc = shouldUseStoredUrl
      ? getStoredIframeUrl(appName, iframeSrc, issuerPath, baseName)
      : iframeSrc;
    return addContextToIframeUrl(baseSrc);
    // initialContextParamsRef.current is intentionally in the deps list below:
    // it's reassigned to a new object (never mutated in place) whenever the
    // hydration effect or the render-time app-switch check updates it, so
    // this recomputes exactly when the baked-in params actually change.
  }, [
    iframeSrc,
    appName,
    issuerPath,
    baseName,
    addContextToIframeUrl,
    initialContextParamsRef.current,
  ]);

  // Persist the last-known iframe URL for this app so a hard refresh restores it.
  const persistIframeUrl = useCallback(
    (url: string) => {
      if (!appName) return;
      if (!isPersistableIframeUrl(url, issuerPath, baseName)) return;
      storageService.setSessionItem(getIframeUrlStorageKey(appName), url);
    },
    [appName, issuerPath, baseName],
  );

  // Clear every persisted per-app iframe URL — used when the child app enters
  // an auth/logout flow so (a) we don't bounce the user back into Keycloak on
  // next refresh and (b) a subsequent user on a shared tab does not inherit
  // the previous user's last-viewed paths in any product.
  const clearStoredIframeUrl = useCallback(() => {
    storageService.removeSessionItemsWithPrefix(
      storageService.KEYS.IFRAME_URL_PREFIX,
    );
  }, []);

  const clearStoredIframeUrlForCurrentApp = useCallback(() => {
    if (!appName) return;
    storageService.removeSessionItem(getIframeUrlStorageKey(appName));
  }, [appName]);

  // If parent route logic provides a different canonical iframe entry URL,
  // invalidate current-app stored URL so stale deep links don't override it.
  useEffect(() => {
    if (prevIframeSrcRef.current === null) {
      // First mount — initialize the ref but don't clear stored URL yet
      prevIframeSrcRef.current = iframeSrc;
      return;
    }
    if (prevIframeSrcRef.current === iframeSrc) return;
    // iframeSrc changed — a genuinely different app is loading; clear its
    // stale stored URL. (initialContextParamsRef is re-captured synchronously
    // during render, above, not here — effects run too late for the memo
    // computed in this same render to pick it up.)
    prevIframeSrcRef.current = iframeSrc;
    clearStoredIframeUrlForCurrentApp();
  }, [iframeSrc, clearStoredIframeUrlForCurrentApp]);

  // Honor explicit browser URL edits: when route/search changes, clear the
  // per-app stored iframe URL and force-load the canonical route-derived src.
  // Dependencies include callbacks to ensure we use the latest src and helper
  // when location changes, but we guard against spurious fires with prevLocationKeyRef.
  useEffect(() => {
    const locationKey = `${location.pathname}${location.search}`;
    if (prevLocationKeyRef.current === null) {
      // First mount — initialize but don't clear yet (memo uses stored URL)
      prevLocationKeyRef.current = locationKey;
      return;
    }
    if (prevLocationKeyRef.current === locationKey) return;

    // Browser URL changed — clear stored URL and load canonical src
    prevLocationKeyRef.current = locationKey;
    clearStoredIframeUrlForCurrentApp();
    setCurrentUrl('');
    setIsIframeLoading(true);

    if (iframeRef.current) {
      // A genuine reload is happening here (explicit URL edit) — refresh the
      // frozen snapshot from live context first, so a loop/teamspace switch
      // that happened since mount is reflected in the reloaded src instead
      // of replaying the stale value captured at mount.
      initialContextParamsRef.current = {
        teamspaceId: contextActiveTeamspaceId || teamspaceCtxId,
        loopId: contextActiveLoopId,
      };
      iframeRef.current.src = addContextToIframeUrl(iframeSrc);
    }
  }, [
    location.pathname,
    location.search,
    iframeSrc,
    addContextToIframeUrl,
    clearStoredIframeUrlForCurrentApp,
    contextActiveTeamspaceId,
    teamspaceCtxId,
    contextActiveLoopId,
  ]);

  // Broadcast context to the already-open child iframe whenever the parent's
  // active context changes (e.g. user navigates to a different loop).
  useEffect(() => {
    if (!iframeHadContent.current) return;
    bridge.broadcastContext();
  }, [
    bridge,
    contextActiveLoopId,
    contextActiveLoopName,
    contextActiveTeamspaceId,
    teamspaceCtxId,
    ctxTeamspaceName,
    appName,
  ]);

  // Set the tab title to the app name from the path, fallback to prop.
  // When syncBrowserUrl is false (e.g. Wiki), the default title from App is kept.
  useEffect(() => {
    if (syncBrowserUrl) {
      const appNameFromPath = extractAppNameFromPath(path);
      document.title = `${capitalizeFirstLetter(appNameFromPath || appName)} - ${t('titleTab')}`;
    }
  }, [path, appName, t, syncBrowserUrl]);

  // Special logic for opening the correct button in the iframe for a specific path
  useEffect(() => {
    if (path !== '/plan/#/ccmweb/my-applications') return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Extract loopName and teamspaceName from getLoopLinksResponse

    const tenant = 'Initial Tenant';
    const buttonId = `id-applist-${tenant}-${teamspaceName}-${loopName}-button`;
    const buttonTestId = `id-applist-${tenant}-${teamspaceName}-${loopName}-button`;

    const onIframeLoad = () => {
      let attempts = 0;
      const maxAttempts = 200;
      const interval = setInterval(() => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!doc) return;
          const button =
            doc.getElementById(buttonId) ||
            doc.querySelector(`button[data-testid="${buttonTestId}"]`);
          if (button) {
            (button as HTMLButtonElement).click();
            clearInterval(interval);
          } else if (++attempts >= maxAttempts) {
            clearInterval(interval);
          }
        } catch {
          clearInterval(interval);
        }
      }, 100);
    };

    // Try immediately in case the button is already present
    onIframeLoad();

    // Also try again on iframe load (for reloads or navigation)
    iframe.addEventListener('load', onIframeLoad);

    return () => {
      iframe.removeEventListener('load', onIframeLoad);
    };
  }, [path, teamspaceName, loopName]);

  // Handle external links in the iframe
  const handleExternalLinks = useCallback(
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'A') {
        const anchorElement = target as HTMLAnchorElement;
        if (anchorElement.target === '_blank') return;
        const anchorHref = anchorElement.href;
        if (anchorHref) {
          try {
            // Reconstruct URL to break taint chain from DOM event target (CWE-601)
            const newUrlObj = new URL(anchorHref);
            const isDifferentOrigin =
              newUrlObj.origin !== window.location.origin;
            if (isDifferentOrigin) {
              // Validate protocol before redirecting to external URL
              if (
                newUrlObj.protocol !== 'https:' &&
                newUrlObj.protocol !== 'http:'
              ) {
                return;
              }
              event.preventDefault();
              const safeUrl = `${newUrlObj.origin}${newUrlObj.pathname}${newUrlObj.search}${newUrlObj.hash}`;
              if (openExternalLinksInNewTab) {
                window.open(safeUrl, '_blank', 'noopener,noreferrer');
              } else {
                // Use reconstructed URL to break taint chain
                window.location.href = safeUrl;
              }
            }
          } catch {
            // Malformed URL — do not navigate
          }
        }
      }
    },
    [openExternalLinksInNewTab],
  );

  // Handle iframe load event
  const handleIframeLoad = () => {
    try {
      if (iframeRef.current?.contentWindow) {
        const newUrl = iframeRef.current.contentWindow.location.href;
        setUrlHistory((prevHistory) => [...prevHistory, newUrl]);
        setCurrentUrl(newUrl);

        // Dismiss loading overlay when a real (non-blank, non-auth) page loads
        if (newUrl !== 'about:blank') {
          const urlObj = new URL(newUrl);
          if (!isAuthPath(urlObj)) {
            setIsIframeLoading(false);
            iframeHadContent.current = true;
            // NEXUS00005880: persist so a browser refresh restores this URL.
            persistIframeUrl(newUrl);
            // Broadcast current context to the freshly loaded child iframe.
            bridge.broadcastContext();
          }
        }

        // Update title based on iframe URL path
        const urlObj = new URL(newUrl);
        // Update the URL for the activity tracker within the iframe for the current app
        if (syncBrowserUrl) {
          window.history.replaceState(null, '', newUrl);
        }
        // Update the appName based on the new URL
        if (syncBrowserUrl) {
          const appNameFromIframe = extractAppNameFromPath(urlObj.pathname);
          document.title = `${capitalizeFirstLetter(appNameFromIframe || appName)} - ${t('titleTab')}`;
        }
      }
    } catch (error) {
      if (error instanceof DOMException) {
        // Cross-origin navigation detected — the iframe left the app's origin.
        // This can be a transient token refresh (child app briefly redirects to
        // Keycloak and back) so we only show the loading overlay here. The
        // polling interval handles the grace period before triggering logout.
        setIsIframeLoading(true);
        if (process.env.NODE_ENV !== 'test') {
          console.error(error);
        }
      } else {
        console.error(error);
      }
    }
  };

  const isAuthPath = (urlObj: URL) => urlObj.pathname.startsWith(issuerPath);

  //TODO Workaround for Keycloak issue where KC_RESTART cookie is lost
  const applyKeyCloakWorkaround = (
    newUrlObj: URL,
    iframeDocument: Document,
  ) => {
    if (isAuthPath(newUrlObj)) {
      if (isSwitcherEnabled) {
        setIsSwitcherEnabled(false);
      }

      // Wire the kc-logout button on every poll until it appears in the DOM.
      // The Keycloak confirmation page may not render the button on the first
      // poll (150 ms), so we keep checking. logoutButtonWiredRef prevents
      // attaching duplicate listeners across multiple poll cycles.
      // TODO: Add SwitcherIframe.test.tsx coverage for the kc-logout click
      //       handler to verify RETURN_URL_KEY is saved to sessionStorage.
      const logoutButton = iframeDocument.getElementById('kc-logout');
      if (logoutButton && !logoutButtonWiredRef.current) {
        logoutButtonWiredRef.current = true;
        logoutButton.addEventListener('click', () => {
          if (redirectRef.current) return;
          redirectRef.current = true;
          clearStoredIframeUrl();
          onLogout();
          try {
            window.sessionStorage.setItem(RETURN_URL_KEY, window.location.href);
          } catch {
            // sessionStorage may be unavailable
          }
          const logoutPath = `${window.location.origin}${baseName}/logged-out`;
          if (window.top !== null) {
            safeNavigateTop(logoutPath);
          }
        });
      }

      const loginButton = iframeDocument.getElementById('login');
      //Override the login button to redirect the top window
      //to the landing page
      if (loginButton) {
        //Note, because we are changing this button from a standard href, we need to handle some of the accessibility features manually
        loginButton.removeAttribute('href');
        loginButton.style.cursor = 'pointer';
        loginButton.setAttribute('role', 'button');
        loginButton.setAttribute('tabindex', '0');
        loginButton.addEventListener('click', (event) => {
          event.preventDefault();
          const loginUrl = `${window.location.origin}${baseName}`;
          if (window.top !== null && isSafeRedirectUrl(loginUrl)) {
            safeNavigateTop(loginUrl);
          }
        });
        loginButton.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const loginUrl = `${window.location.origin}${baseName}`;
            if (window.top !== null && isSafeRedirectUrl(loginUrl)) {
              safeNavigateTop(loginUrl);
            }
          }
        });
      }
    } else if (isSwitcherEnabled === false) {
      setIsSwitcherEnabled(true);
    }
  };

  // Main effect for iframe event listeners and URL polling
  useEffect(() => {
    const iframe = iframeRef.current;
    // Re-show the loading overlay the instant the child app starts navigating
    // away, before the next document begins to paint. This closes the white-
    // screen flash window that occurs between a navigation click in the child
    // and the next iframe `load` event.
    const handleNavStart = () => setIsIframeLoading(true);
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad);
      try {
        iframe.contentWindow?.addEventListener(
          'mousemove',
          resetInactivityTimer,
        );
        iframe.contentWindow?.addEventListener('keydown', resetInactivityTimer);
        iframe.contentWindow?.addEventListener('click', resetInactivityTimer);
        iframe.contentWindow?.addEventListener('click', handleExternalLinks);
        iframe.contentWindow?.addEventListener('keydown', handleExternalLinks);
        iframe.contentWindow?.addEventListener('beforeunload', handleNavStart);
        iframe.contentWindow?.addEventListener('pagehide', handleNavStart);
      } catch {
        // Cross-origin contentWindow access throws — safe to ignore; the load
        // handler / polling interval still detect the navigation.
      }
    }

    const intervalId = setInterval(() => {
      //If we have already redirected, we don't want to do any of this again
      //and we will just let the component unload
      //This is to prevent the component from redirecting multiple times
      //before it has a chance to unload
      if (redirectRef.current) return;

      try {
        if (iframeRef.current?.contentWindow) {
          const newUrl = iframeRef.current.contentWindow.location.href;
          if (newUrl === 'about:blank') return;
          const newUrlObj = new URL(newUrl);

          if (newUrl !== currentUrl) {
            setUrlHistory((prevHistory) => [...prevHistory, newUrl]);
            setCurrentUrl(newUrl);
            // Update the URL for the activity tracker within the iframe for the current app
            if (syncBrowserUrl) {
              window.history.replaceState(null, '', newUrl);
            }
            // NEXUS00005880: persist the latest in-app URL so a browser
            // refresh restores the user's current location.
            // Skip auth URLs — we don't want to restore a Keycloak URL on refresh.
            if (!isAuthPath(newUrlObj)) {
              persistIframeUrl(newUrl);
            }
            // Update the appName based on the new URL
            if (syncBrowserUrl) {
              const appNameFromIframe = extractAppNameFromPath(
                newUrlObj.pathname,
              );
              document.title = `${capitalizeFirstLetter(appNameFromIframe || appName)} - ${t('titleTab')}`;
            }
          }
          applyKeyCloakWorkaround(
            newUrlObj,
            iframeRef.current.contentWindow.document,
          );

          // Track when the iframe has loaded real (non-auth) content at least once.
          // Also reset the logout button wire flag so that if the user cancelled
          // a Keycloak logout and tries again, the new kc-logout DOM node gets
          // a fresh listener on the next auth-path poll cycle.
          if (!isAuthPath(newUrlObj)) {
            iframeHadContent.current = true;
            logoutButtonWiredRef.current = false;
          }

          if (isAuthPath(newUrlObj)) {
            // Keycloak auth page detected. Keep the spinner until the
            // confirmation page has rendered its kc-logout button — then
            // dismiss the overlay so the user can see and click it.
            const hasLogoutButton =
              !!iframeRef.current.contentWindow.document.getElementById(
                'kc-logout',
              );
            setIsIframeLoading(!hasLogoutButton);
          } else if (isIframeLoading) {
            setIsIframeLoading(false);
          }
        }
      } catch (error) {
        // Cross-origin DOMException — iframe navigated away from the app origin
        // (likely to Keycloak for login or logout). Show the loading overlay;
        // applyKeyCloakWorkaround will handle the buttons once the page loads.
        if (error instanceof DOMException) {
          setIsIframeLoading(true);
        }
      }
    }, URL_CHANGE_INTERVAL);

    return () => {
      if (iframe) {
        iframe.removeEventListener('load', handleIframeLoad);
        try {
          iframe.contentWindow?.removeEventListener(
            'mousemove',
            resetInactivityTimer,
          );
          iframe.contentWindow?.removeEventListener(
            'keydown',
            resetInactivityTimer,
          );
          iframe.contentWindow?.removeEventListener(
            'click',
            resetInactivityTimer,
          );
          iframe.contentWindow?.removeEventListener(
            'click',
            handleExternalLinks,
          );
          iframe.contentWindow?.removeEventListener(
            'keydown',
            handleExternalLinks,
          );
          iframe.contentWindow?.removeEventListener(
            'beforeunload',
            handleNavStart,
          );
          iframe.contentWindow?.removeEventListener('pagehide', handleNavStart);
        } catch {
          // Cross-origin contentWindow access can throw on teardown.
        }
      }
      clearInterval(intervalId);
    };
  }, [
    currentUrl,
    resetInactivityTimer,
    handleExternalLinks,
    isIframeLoading,
    appName,
    path,
    persistIframeUrl,
  ]);

  return (
    <div
      style={{
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
      }}
      id="switcher-iframe-container"
    >
      {isSwitcherEnabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            marginTop: '6px',
            ...(resolvedSwitcherPosition === 'left'
              ? { left: 0 }
              : { right: 0 }),
            transition: 'transform linear',
            zIndex: 10,
          }}
          id="switcher-container"
        >
          <Switcher hasNexusRole={true} t={(key: string) => key} />
        </div>
      )}
      <iframe
        title="solution-iframe"
        ref={iframeRef}
        src={sanitizeIframeSrc(iframeSrcWithParams, allowedIframeOrigins)}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          opacity: isIframeLoading ? 0 : 1,
          transition: 'opacity 0.15s ease-in',
        }}
        allowFullScreen
        sandbox="allow-scripts allow-same-origin allow-downloads allow-forms allow-popups allow-modals allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation"
        id="solution-iframe"
      />
      {isIframeLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-white dark:bg-[#161616]"
          style={{ zIndex: 5 }}
          data-testid="iframe-loading-overlay"
          aria-live="polite"
          aria-busy="true"
          aria-label={t('loading')}
        >
          <ProgressSpinner style={{ width: '80px', height: '80px' }} />
        </div>
      )}
    </div>
  );
};

export default SwitcherIframe;
